import PostAddIcon from "@mui/icons-material/PostAdd";
import { Button, FormControl } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React from "react";
import { type Control, Controller } from "react-hook-form";

import { AdjustmentType, PaymentType, ReviewStatus } from "../../status";
import { renderField } from "../ClaimsTable/FormFieldRenderer";
import { InputWrapper } from "../CredentialsDashboard/ui/InputWrapper";
import { ErrorHelperText } from "../PatientsDashboard/ui/ErrorHelper";
import UploadFile from "../UploadFile";
import {
  type AddPaymentFormFields,
  editablePaymentFields,
} from "./CrudForms.zod";

const ALLOWED_FILE_TYPES = ["application/pdf"];

interface PaymentFormProps {
  control: Control<AddPaymentFormFields>;
  practices: PracticeMessage[];
  supportedPayers: CredentialsSupportedPayersMessage[];
  supportedSources: SourceMessage[];
  setPaymentFileToUpload: (file: File | null) => void;
  paymentFileToUpload: File | null;
  setFileError: (error: string | null) => void;
  fileError: string | null;
  isEdit: boolean;
}

export default function PaymentForm({
  control,
  practices,
  supportedPayers,
  supportedSources,
  setPaymentFileToUpload,
  paymentFileToUpload,
  setFileError,
  fileError,
  isEdit,
}: PaymentFormProps) {
  const theme = useTheme();
  const regularFields = editablePaymentFields
    .filter((field) => !["dropdown", "radio"].includes(field.type))
    .filter((field) => field.name !== "wieldyId");
  const [showUpload, setShowUpload] = React.useState(false);

  return (
    <>
      <Typography variant="h5" sx={{ fontWeight: "600" }}>
        Payment Details
      </Typography>
      <Box sx={{ margin: `${theme.spacing(3)} 0` }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ErrorHelperText message={fileError || undefined} />
            {isEdit && !showUpload && (
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setShowUpload(true)}
                startIcon={<PostAddIcon />}
              >
                Add EOB (Optional)
              </Button>
            )}
            {(!isEdit || showUpload) && (
              <UploadFile
                file={paymentFileToUpload}
                setFile={setPaymentFileToUpload}
                onError={setFileError}
                allowedFileTypes={ALLOWED_FILE_TYPES}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="reviewStatus"
              control={control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <FormControl sx={{ width: "100%" }}>
                  <InputWrapper>
                    <TextField
                      select
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value ?? ""}
                      name="reviewStatus"
                      inputRef={ref}
                      label="Review Status"
                      variant="outlined"
                      fullWidth
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      {Object.values(ReviewStatus).map((status) => (
                        <MenuItem key={status} value={status}>
                          {status}
                        </MenuItem>
                      ))}
                    </TextField>
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="practiceId"
              control={control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <FormControl sx={{ width: "100%" }}>
                  <InputWrapper>
                    <TextField
                      select
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value ?? ""}
                      name="practiceId"
                      inputRef={ref}
                      label="Practice"
                      variant="outlined"
                      fullWidth
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      {practices.map((practice) => (
                        <MenuItem
                          key={practice.wieldyId}
                          value={practice.wieldyId}
                        >
                          {practice.displayName}
                        </MenuItem>
                      ))}
                    </TextField>
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="source"
              control={control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <FormControl sx={{ width: "100%" }}>
                  <InputWrapper>
                    <TextField
                      select
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value ?? ""}
                      name="source"
                      inputRef={ref}
                      label="Source"
                      variant="outlined"
                      fullWidth
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      {supportedSources.map((source) => (
                        <MenuItem key={source.id} value={source.name}>
                          {source.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="payer"
              control={control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <FormControl sx={{ width: "100%" }}>
                  <InputWrapper>
                    <TextField
                      select
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value ?? ""}
                      name="payer"
                      inputRef={ref}
                      label="Payer"
                      variant="outlined"
                      fullWidth
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      {supportedPayers.map((payer) => (
                        <MenuItem key={payer.wieldyId} value={payer.name}>
                          {payer.displayLabel}
                        </MenuItem>
                      ))}
                    </TextField>
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="paymentType"
              control={control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <FormControl sx={{ width: "100%" }}>
                  <InputWrapper>
                    <TextField
                      select
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value ?? ""}
                      name="paymentType"
                      inputRef={ref}
                      label="Payment Type"
                      variant="outlined"
                      fullWidth
                      required
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      <MenuItem key={PaymentType.EFT} value={PaymentType.EFT}>
                        {PaymentType.EFT}
                      </MenuItem>
                    </TextField>
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
          </Grid>
          {regularFields.map((field) =>
            renderField<AddPaymentFormFields>(control, field),
          )}
          <Grid item xs={12} sm={6}>
            <Controller
              name="adjustmentType"
              control={control}
              render={({
                field: { onChange, onBlur, value, ref },
                fieldState: { error },
              }) => (
                <FormControl sx={{ width: "100%" }}>
                  <InputWrapper>
                    <TextField
                      select
                      onChange={onChange}
                      onBlur={onBlur}
                      value={value ?? ""}
                      name="adjustmentType"
                      inputRef={ref}
                      label="EOP Payment Adjustment Type"
                      variant="outlined"
                      fullWidth
                      required={false}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    >
                      {Object.values(AdjustmentType).map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                    <ErrorHelperText message={error?.message} />
                  </InputWrapper>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
